import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MOVE_STRINGS } from '../../constants';

import { Fireworks } from 'modules/daily-quests-page/components/fireworks/fireworks';

import ModalWithFullScreenBg from 'shared/components/ui/Modal/ModalWithFullScreenBg';
import { ButtonWithLoader } from 'shared/components/ui/PrimaryButton/button-with-loader';
import SecondaryBtn from 'shared/components/ui/SecondaryBtn/SecondaryBtn';
import { ImageData, OptimizedImage } from 'shared/components/ui/optimized-image/optimized-image';
import { MovesEnum } from 'shared/constants/shared-enums';
import { useAccount, usePublicClient, useWalletClient } from 'shared/hooks/use-web3';
import { useQuestsStore } from 'shared/store/questsStore';

import closeX from 'assets/svg/close-x.svg';

import { CardsBlock } from './cards-block';

interface IMintModal {
  open: boolean;
  onClose: () => void;
  cardToMint: ImageData;
  max: number;
  move: MovesEnum;
}

export const MintModal: FC<IMintModal> = ({ open, onClose, cardToMint, max, move }) => {
  const { address } = useAccount();
  const publicClient = usePublicClient();
  const walletClient = useWalletClient();
  const [count, setCount] = useState(1);
  const maxToMint = useMemo(() => Math.max(0, Math.floor((max ?? 0) / 100)), [max]);

  const [loaderFinished, setLoaderFinished] = useState(false);
  const [showFireworks, setShowFireworks] = useState(false);
  const onCloseHandler = useCallback(() => {
    setLoaderFinished(false);
    onClose();
  }, [onClose]);
  const { t } = useTranslation();
  const tRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { mintNft, isMinting, minted, mintError } = useQuestsStore();

  const inc = useCallback(() => setCount((c) => Math.min(c + 1, maxToMint)), [maxToMint]);
  const dec = useCallback(() => setCount((c) => Math.max(c - 1, 0)), []);

  const handleMintClick = useCallback(async () => {
    if (!publicClient || !walletClient || !address || !move) return;
    await mintNft(publicClient, walletClient, address as `0x${string}`, move, count);
  }, [mintNft, publicClient, walletClient, address, move, count]);

  const onEndAnimation = useCallback(async () => {
    setShowFireworks(true);
    setLoaderFinished(true);
  }, []);

  useEffect(() => {
    if (showFireworks) {
      tRef.current = setTimeout(() => {
        setShowFireworks(false);
      }, 1500);
    }
  }, [showFireworks]);

  useEffect(() => {
    return () => {
      if (tRef.current) clearTimeout(tRef.current);
    };
  }, []);

  useEffect(() => {
    if (open) {
      setCount(1);
    }
  }, [open]);

  return (
    <ModalWithFullScreenBg
      open={open}
      className="bg-background/60"
      withGridBackground={false}
      childrenClassName={'border-[2px] border-border sm:border-[3px] max-w-[25rem]'}
    >
      <div className="rounded-3xl bg-[linear-gradient(180deg,_#120F34_0%,_#401F07_100%)] pt-[1.125rem]">
        {mintError ? (
          <h2 className="bg-gradient-to-b from-[#FFEAE0] to-[#FFBE9E] bg-clip-text text-center text-xl font-extrabold text-transparent">
            {t('nft.errorMinting')}
          </h2>
        ) : (
          <h2 className="bg-gradient-to-b from-[#FFEAE0] to-[#FFBE9E] bg-clip-text text-center text-xl font-extrabold text-transparent">
            {minted ? 'Congratulations' : 'Bronze Mint'}
          </h2>
        )}

        <button onClick={onCloseHandler} className="absolute right-4 top-4">
          <img src={closeX} alt="Close" className="h-6 w-6" />
        </button>

        {/* Content */}
        <div className="mb-4 grid grid-cols-[6.625rem,1fr] gap-2 p-4">
          <div className="relative">
            <OptimizedImage
              imageData={cardToMint}
              alt="Result card"
              className="h-[161px] w-[106px]"
            />
            {showFireworks && minted && <Fireworks />}
            {!!count && (
              <span className="absolute bottom-[1rem] right-[-0.5rem] flex h-9 w-9 items-center justify-center rounded-full border-[3px] border-[#C0820B] bg-[linear-gradient(180deg,_#FFC000_0%,_#FF8B00_100%)] text-[1rem] font-semibold text-[#6A3B09]">
                +{count}
              </span>
            )}
          </div>

          {/* Right: materials + controls */}
          {minted && loaderFinished && !mintError ? (
            <div className="relative flex h-full max-h-[10rem] w-full flex-col items-center justify-center rounded-xl border border-indigo bg-[#05055B] text-xl">
              <span className="bg-gradient-to-b from-white to-[#B5E0FF] bg-clip-text text-center text-xl font-extrabold text-transparent">
                {t('nft.youveMinted')}
              </span>
              <span className="bg-gradient-to-b from-[#FFEAE0] to-[#FFBE9E] bg-clip-text text-center text-xl font-extrabold capitalize text-transparent">
                Bronze {MOVE_STRINGS[move]}
              </span>
            </div>
          ) : (
            <CardsBlock
              count={count}
              max={max}
              cardToMint={cardToMint}
              dec={dec}
              inc={inc}
              disabled={isMinting}
              multiplyTo100={true}
            />
          )}
        </div>

        {/* Bottom bar with Forge button */}
        <div className="w-full rounded-b-3xl border-t-4 border-border bg-[linear-gradient(180deg,_#060771_0%,_#080542_100%)] px-4 pb-4 pt-3">
          <div className="relative">
            {!loaderFinished ? (
              <ButtonWithLoader
                text="Mint"
                onClick={handleMintClick}
                disabled={count <= 0}
                className="relative w-full"
                onEnd={onEndAnimation}
                isLoading={isMinting}
              />
            ) : (
              <SecondaryBtn
                text={t('nft.backToCollection')}
                onClick={onCloseHandler}
                className="relative w-full"
              />
            )}
          </div>
        </div>
      </div>
    </ModalWithFullScreenBg>
  );
};
